import { TabContext } from '@mui/lab';
import { ChangeEvent, useState } from 'react';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { useTranslate, useGetMany } from 'react-admin';
import {
  CategoryRecord,
  HotelRecord,
  PageRecordLocalized,
  Timeframe,
} from '../../../common/types';
import UsageTotalsKeenChart from '../UsageTotalsKeenChart';
import { useIntl } from '../../../context/intlContext';
import {
  usePageViewsByCategoryData,
  usePageViewsHourlyAverageData,
  usePageViewsByPageIdData,
  usePageViewsTotalData,
  usePageViewsWeekdayAverageData,
} from '../data-hooks';
import ChartTab from '../shared/ChartTab';
import ChartListTab from '../shared/ChartListTab';
import ChartsCircularProgress from '../shared/ChartsCircularProgress';
import ChartListTabList from '../shared/ChartListTabList';
import ChartTabPanel from '../shared/ChartTabPanel';
import ChartList from '../shared/ChartList';
import ChartMainTabList from '../shared/ChartMainTabList';
import ChartTooltip from '../shared/ChartTooltip';
import SharedListItemProgressBar from '../shared/ListItemProgressBar';
import ChartListItem from '../shared/ChartListItem';
import ChartListItemTitle from '../shared/ChartListItemTitle';
import ChartListItemValue from '../shared/ChartListItemValue';
import ChartListCategoryTitle from '../shared/ChartListCategoryTitle';
import VerticalDivider from '../shared/VerticalDivider';
import ListSectionContainer from '../shared/ListSectionContainer';
import ChartContainer from '../shared/ChartContainer';
import ChartSideList from '../shared/ChartSideList';
import ChartContent from '../shared/ChartContent';

const ListItemProgressBar = (props: {
  max: string | number;
  value: string | number;
  color?: string;
}) => (
  <SharedListItemProgressBar
    color="rgba(255, 161, 145, 0.16)"
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  />
);

const PageViewsList = ({
  categories,
  hotel,
  timeframe,
  onClick,
}: {
  categories: CategoryRecord[];
  hotel: HotelRecord;
  timeframe: Timeframe;
  onClick: Function;
}) => {
  const t = useTranslate();
  const intl = useIntl();

  const { data: pageViewsByCategory, loading } = usePageViewsByCategoryData({
    hotel,
    timeframe,
  });

  const folders = pageViewsByCategory
    .map(({ categoryId, result }) => {
      const categoryRecord = categories.find(({ id }) => +categoryId === id);

      return {
        id: categoryId,
        title: categoryRecord
          ? categoryRecord.title
          : t('charts.deleted-folder', { id: categoryId }),
        views: result,
        category: categoryRecord,
      };
    })
    .sort((a, b) => b.views - a.views);

  const maxViews = Math.max(...folders.map(({ views }) => views));

  if (loading) {
    return <ChartsCircularProgress />;
  }

  return (
    <ChartList>
      {folders.map((folder) => (
        <ChartListItem
          clickable
          key={folder.id}
          onClick={() => onClick(folder.category)}
          data-testid="page-view-list-item"
        >
          <ChartTooltip title={folder.title}>
            <ChartListItemTitle>{folder.title}</ChartListItemTitle>
          </ChartTooltip>
          <ChartListItemValue>
            {intl.formatNumber(folder.views)}
          </ChartListItemValue>
          <KeyboardArrowRightIcon fontSize="small" />
          <ListItemProgressBar max={maxViews} value={folder.views} />
        </ChartListItem>
      ))}
    </ChartList>
  );
};

const PageViewsItemList = ({
  selectedCategory,
  hotel,
  timeframe,
  categories = [],
  withCategoryTitle = false,
}: {
  selectedCategory: CategoryRecord | null;
  hotel: HotelRecord;
  timeframe: Timeframe;
  categories?: CategoryRecord[];
  withCategoryTitle?: boolean;
}) => {
  const intl = useIntl();

  const {
    data: pageViewsByPageData,
    loading: isPageViewsByPageDataLoading,
  } = usePageViewsByPageIdData({
    hotel,
    timeframe,
    selectedCategory,
  });

  const totalResultByPageId = pageViewsByPageData.reduce(
    (acc: { [key: number]: number }, val) => {
      if (!acc[+val.pageId]) {
        acc[+val.pageId] = 0;
      }

      acc[+val.pageId] += val.result;

      return acc;
    },
    {}
  );
  const pageIds = Object.keys(totalResultByPageId);

  const {
    data: pages = [],
    isLoading: pagesLoading,
  } = useGetMany<PageRecordLocalized>(
    'pages',
    { ids: pageIds },
    {
      enabled: !!pageIds.length,
    }
  );

  const isLoading = pagesLoading || isPageViewsByPageDataLoading;

  // See https://github.com/marmelab/react-admin/issues/5054
  const pageViewsData = pages
    .filter((page) => page !== undefined)
    .map(({ id, title, categoryId }) => {
      const categoryRecord = categories.find(
        ({ id: catId }) => catId === categoryId
      );

      return {
        id,
        title,
        categoryTitle: categoryRecord?.title,
        total: totalResultByPageId[id as number],
      };
    });

  const sortedPages = pageViewsData.sort((a, b) => b.total - a.total);
  const maxOrder = Math.max(...sortedPages.map(({ total }) => total));

  if (isLoading) {
    return <ChartsCircularProgress />;
  }

  return (
    <ChartList>
      {sortedPages.map((page) => (
        <ChartListItem key={page.id}>
          <ChartTooltip title={<span>{page.title}</span>}>
            <ChartListItemTitle>{page.title}</ChartListItemTitle>
          </ChartTooltip>
          {withCategoryTitle && (
            <ChartTooltip title={page.categoryTitle || ''}>
              <ChartListCategoryTitle>
                {page.categoryTitle}
              </ChartListCategoryTitle>
            </ChartTooltip>
          )}
          <ChartListItemValue>
            {intl.formatNumber(page.total)}
          </ChartListItemValue>
          <ListItemProgressBar max={maxOrder} value={page.total} />
        </ChartListItem>
      ))}
    </ChartList>
  );
};

const ListSection = ({
  categories,
  hotel,
  timeframe,
  selectedCategory,
  onClick,
}: {
  categories: CategoryRecord[];
  hotel: HotelRecord;
  timeframe: Timeframe;
  selectedCategory: CategoryRecord | null;
  onClick: Function;
}) => {
  const t = useTranslate();
  const [currentListTab, setCurrentListTab] = useState('1');

  const onCurrentListTabChange = (
    _event: ChangeEvent<{}>,
    newValue: string
  ) => {
    setCurrentListTab(newValue);
  };

  return (
    <ListSectionContainer>
      <TabContext value={currentListTab}>
        <ChartListTabList onChange={onCurrentListTabChange}>
          {selectedCategory ? (
            <ChartListTab
              value="1"
              label={
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                  }}
                >
                  <KeyboardArrowLeftIcon fontSize="small" />
                  <VerticalDivider style={{ margin: '6px 10px 6px 5px' }} />
                  {selectedCategory.title}
                </div>
              }
              onClick={() => onClick(null)}
            />
          ) : (
            [
              <ChartListTab
                key={0}
                value="1"
                label={t('charts.category-options.all-folders')}
              />,
              <VerticalDivider key={1} style={{ margin: '24px 16px' }} />,
              <ChartListTab
                key={2}
                value="2"
                label={t('charts.category-options.all-pages')}
              />,
            ]
          )}
        </ChartListTabList>
        <ChartTabPanel value="1">
          {selectedCategory ? (
            <PageViewsItemList
              selectedCategory={selectedCategory}
              hotel={hotel}
              timeframe={timeframe}
            />
          ) : (
            <PageViewsList
              categories={categories}
              hotel={hotel}
              timeframe={timeframe}
              onClick={onClick}
            />
          )}
        </ChartTabPanel>
        <ChartTabPanel value="2">
          <PageViewsItemList
            withCategoryTitle
            selectedCategory={null}
            categories={categories}
            hotel={hotel}
            timeframe={timeframe}
          />
        </ChartTabPanel>
      </TabContext>
    </ListSectionContainer>
  );
};

const UsageTotalsChartPageViews = ({
  categories,
  hotel,
  timeframe,
}: {
  categories: CategoryRecord[];
  hotel: HotelRecord;
  timeframe: Timeframe;
}) => {
  const t = useTranslate();
  const [currentChartTab, setCurrentChartTab] = useState('1');
  const [
    selectedCategory,
    setSelectedCategory,
  ] = useState<CategoryRecord | null>(null);

  const {
    loading: isPageViewsTotalDataLoading,
    chartProps: pageViewsTotalDataChartProps,
  } = usePageViewsTotalData({ hotel, timeframe, selectedCategory });
  const {
    chartProps: pageViewsWeekdayAverageDataChartProps,
    loading: isPageViewsWeekdayAverageDataLoading,
  } = usePageViewsWeekdayAverageData({ hotel, timeframe, selectedCategory });
  const {
    chartProps: pageViewsHourlyAverageDataChartProps,
    loading: isPageViewsHourlyAverageDataLoading,
  } = usePageViewsHourlyAverageData({ hotel, timeframe, selectedCategory });

  const onCurrentChartTabChange = (
    _event: ChangeEvent<{}>,
    newValue: string
  ) => {
    setCurrentChartTab(newValue);
  };

  const handleClick = (category: CategoryRecord | null) =>
    setSelectedCategory(category);

  return (
    <ChartContainer>
      <ChartSideList>
        <ListSection
          categories={categories}
          hotel={hotel}
          timeframe={timeframe}
          selectedCategory={selectedCategory}
          onClick={handleClick}
        />
      </ChartSideList>
      <ChartContent>
        <TabContext value={currentChartTab}>
          <ChartMainTabList onChange={onCurrentChartTabChange}>
            <ChartTab value="1" label={t('charts.usage.chart-types.totals')} />
            <ChartTab
              value="2"
              label={t('charts.usage.chart-types.weekday-average')}
            />
            <ChartTab
              value="3"
              label={t('charts.usage.chart-types.hourly-average')}
            />
          </ChartMainTabList>
          <ChartTabPanel value="1">
            {isPageViewsTotalDataLoading ? (
              <ChartsCircularProgress />
            ) : (
              <UsageTotalsKeenChart
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...pageViewsTotalDataChartProps}
              />
            )}
          </ChartTabPanel>
          <ChartTabPanel value="2">
            {isPageViewsWeekdayAverageDataLoading ? (
              <ChartsCircularProgress />
            ) : (
              <UsageTotalsKeenChart
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...pageViewsWeekdayAverageDataChartProps}
              />
            )}
          </ChartTabPanel>
          <ChartTabPanel value="3">
            {isPageViewsHourlyAverageDataLoading ? (
              <ChartsCircularProgress />
            ) : (
              <UsageTotalsKeenChart
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...pageViewsHourlyAverageDataChartProps}
              />
            )}
          </ChartTabPanel>
        </TabContext>
      </ChartContent>
    </ChartContainer>
  );
};

export default UsageTotalsChartPageViews;
